import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { Container, Display, Lead, Pill, CtaLink, Phone, BrowserFrame } from '@/components/system';

/**
 * Home hero — Apple idiom: one headline, one sentence, two actions, then the product.
 * The phones show REAL Mi Admin screens (never AI-rendered UI).
 */
export function Hero() {
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.from('[data-hero-copy] > *', { y: 24, opacity: 0, duration: 0.7, stagger: 0.08, delay: 0.1 })
        .from('[data-phone]', { y: 80, opacity: 0, duration: 0.9, stagger: 0.1 }, '-=0.4');
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={rootRef} className="relative w-full bg-white overflow-hidden pt-[120px] sm:pt-[140px]">
      <Container>
        <div data-hero-copy className="mx-auto max-w-4xl text-center">
          <p className="text-base sm:text-lg font-semibold text-brand mb-4">
            Software para agencias de envíos y transitarias a Cuba
          </p>
          <Display as="h1" size="xl">
            Toda tu agencia en un solo sistema.
          </Display>
          <Lead className="mt-6 mx-auto max-w-2xl">
            Panel web para tu equipo, app para tus clientes y app de almacén y reparto —
            conectados en tiempo real. Sin cadenas de WhatsApp, sin hojas de Excel.
          </Lead>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <Pill to="/plataforma">Solicita una demo</Pill>
            <CtaLink to="/como-funciona">Ver cómo funciona</CtaLink>
          </div>
        </div>
      </Container>

      {/* Product: the platform in one image — real Mi Dashboard panel flanked by the
          real customer and field apps. Bleeds into the grey band below. */}
      <div className="relative mt-14 sm:mt-20 h-[420px] sm:h-[480px] lg:h-[560px]">
        <div className="absolute inset-x-0 top-0 flex items-start justify-center">
          <div data-phone className="hidden md:block relative z-10 translate-y-24 lg:translate-y-28 -mr-14 lg:-mr-20">
            <Phone src="/screens/mec-user-home.png" alt="Mi Envío Cuba — inicio con el estado de tus envíos activos" width={230} priority />
          </div>
          <div data-phone className="relative w-[min(92vw,760px)]">
            <BrowserFrame
              src="/screens/mi-dashboard-panel.jpg"
              alt="Mi Dashboard — panel de la agencia con el resumen del día, actividad y paquetes recientes"
              width={2240}
              height={1254}
              priority
            />
          </div>
          <div data-phone className="hidden md:block relative z-10 translate-y-24 lg:translate-y-28 -ml-14 lg:-ml-20">
            <Phone src="/screens/mi-admin-home.png" alt="Mi Admin — inicio con el resumen del día del repartidor" width={230} priority />
          </div>
        </div>
        {/* fade into the grey band below */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent via-white/70 to-bg-light" />
      </div>
    </section>
  );
}
